// Select field - mode-aware (renders readonly in view modes)
import type React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Select, FieldError, FieldWrapper, FieldDisplay } from '@startus/ui';
import { useFormMode } from '../../hooks/useFormMode';
import type { BaseFieldProps } from '../../types/fields';

// Helper to get nested error from formState.errors using dot notation path
function getNestedError(errors: Record<string, unknown>, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = errors;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return (current as { message?: string })?.message;
}

export function SelectField({ name, label, placeholder, required, options = [] }: BaseFieldProps) {
  const { t } = useTranslation('common');
  const { isReadOnly } = useFormMode();
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);
  const error = getNestedError(errors, name);

  // Find display label for current value
  const displayValue = options.find((opt) => opt.value === value)?.label || value;

  if (isReadOnly) {
    return <FieldDisplay label={label} value={displayValue} required={required} />;
  }

  return (
    <FieldWrapper>
      {/*<Label required={required} fieldNumber={fieldNumber}>{label}</Label>*/}
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field }) => (
          <Select
            ref={field.ref as React.Ref<HTMLButtonElement>}
            name={field.name}
            value={field.value ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            options={options.map((opt) => ({ value: opt.value, label: opt.label }))}
            placeholder={placeholder || t('common.select')}
            error={!!error}
          />
        )}
      />
      <FieldError message={error} />
    </FieldWrapper>
  );
}
